// src/pages/DiemDanh/DiemDanhHistory.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../../config/firebase";
import BottomSheet from "../../components/BottomSheet/BottomSheet";
import { formatDate } from "../../utils";
import { STATUS_STYLE } from "./DiemDanh";

const MONTH_NAMES = ['', 'T1', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'T8', 'T9', 'T10', 'T11', 'T12'];

const DiemDanhHistory = ({ show, onClose, selectedHS, fullScreen }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [allData, setAllData] = useState([]);
  const [stats, setStats] = useState({ tong: 0, coMat: 0, vangPhep: 0, vangKP: 0, diTre: 0 });
  const [filter, setFilter] = useState('all');
  const [timeFilter, setTimeFilter] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [showAllMobile, setShowAllMobile] = useState(false);

  useEffect(() => {
    if ((show || fullScreen) && selectedHS) loadData();
  }, [show, fullScreen, selectedHS, filter, timeFilter, selectedMonth, selectedYear]);

  const loadData = async () => {
    if (!selectedHS) return;
    setLoading(true);
    try {
      const snap = await getDocs(query(collection(db, "diemDanh"), where("hocSinhId", "==", selectedHS.id)));
      let data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      
      if (filter === 'hk1') data = data.filter(d => { const m = new Date(d.ngayHoc).getMonth() + 1; return m >= 8 && m <= 12; });
      else if (filter === 'hk2') data = data.filter(d => { const m = new Date(d.ngayHoc).getMonth() + 1; return m >= 1 && m <= 5; });
      
      if (timeFilter === 'month') {
        data = data.filter(d => {
          const date = new Date(d.ngayHoc);
          return date.getMonth() + 1 === selectedMonth && date.getFullYear() === selectedYear;
        });
      }
      
      data.sort((a, b) => b.ngayHoc.localeCompare(a.ngayHoc));
      setAllData(data);
      setStats({
        tong: data.length,
        coMat: data.filter(d => d.trangThai === 'coMat' || d.trangThai === 'diTre').length,
        vangPhep: data.filter(d => d.trangThai === 'vangPhep').length,
        vangKP: data.filter(d => d.trangThai === 'vangKhongPhep').length,
        diTre: data.filter(d => d.trangThai === 'diTre').length,
      });
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const chuyenCan = stats.tong > 0 ? Math.round((stats.coMat / stats.tong) * 100) : 0;
  const displayMobile = showAllMobile ? allData : allData.slice(0, 10);

  // Nội dung chung
  const content = (
    <div className="space-y-4">
      {/* STATS */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 md:gap-3">
        <div className="col-span-2 md:col-span-1 bg-primary-container text-on-primary-container rounded-xl p-4 flex items-center justify-between relative overflow-hidden">
          <div><p className="text-label-caps opacity-80 mb-1">Chuyên cần</p><p className="text-[36px] font-extrabold leading-none">{chuyenCan}<span className="text-[18px] opacity-60">%</span></p></div>
        </div>
        <div className="bg-surface-container-lowest rounded-xl p-3 border text-center"><p className="text-label-caps text-on-surface-variant">Tổng</p><p className="text-2xl font-bold text-primary">{stats.tong}</p></div>
        <div className="bg-surface-container-lowest rounded-xl p-3 border text-center"><p className="text-label-caps text-on-surface-variant">Có mặt</p><p className="text-2xl font-bold text-green-600">{stats.coMat}</p></div>
        <div className="bg-surface-container-lowest rounded-xl p-3 border text-center"><p className="text-label-caps text-on-surface-variant">Vắng phép</p><p className="text-2xl font-bold text-yellow-600">{stats.vangPhep}</p></div>
        <div className="bg-surface-container-lowest rounded-xl p-3 border text-center"><p className="text-label-caps text-on-surface-variant">Vắng KP</p><p className="text-2xl font-bold text-red-600">{stats.vangKP}</p></div>
      </div>

      {/* FILTERS */}
      <div className="space-y-2">
        <div className="flex gap-1.5 flex-wrap">
          {[{ key: 'all', label: 'Tất cả' }, { key: 'hk1', label: 'HK1' }, { key: 'hk2', label: 'HK2' }].map(f => (
            <button key={f.key} onClick={() => { setFilter(f.key); setTimeFilter('all'); }}
              className={`px-3 py-1.5 rounded-full text-caption font-bold ${filter === f.key ? 'bg-primary text-on-primary' : 'bg-surface-container text-on-surface-variant'}`}>{f.label}</button>
          ))}
        </div>
        <div className="flex gap-1.5 flex-wrap items-center">
          <button onClick={() => setTimeFilter(timeFilter === 'month' ? 'all' : 'month')}
            className={`px-3 py-1.5 rounded-full text-caption font-bold flex items-center gap-1 ${timeFilter === 'month' ? 'bg-primary text-on-primary' : 'bg-surface-container text-on-surface-variant'}`}>
            <span className="material-symbols-outlined text-[14px]">calendar_month</span>
            {timeFilter === 'month' ? `${MONTH_NAMES[selectedMonth]}/${selectedYear}` : 'Theo tháng'}
          </button>
          {timeFilter === 'month' && (
            <>
              <select value={selectedMonth} onChange={e => setSelectedMonth(parseInt(e.target.value))} className="bg-surface border border-outline-variant rounded-full px-2 py-1.5 text-caption font-bold outline-none">
                {MONTH_NAMES.map((m, i) => i > 0 && <option key={i} value={i}>{m}</option>)}
              </select>
              <select value={selectedYear} onChange={e => setSelectedYear(parseInt(e.target.value))} className="bg-surface border border-outline-variant rounded-full px-2 py-1.5 text-caption font-bold outline-none">
                {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </>
          )}
        </div>
      </div>

      {/* DESKTOP TABLE */}
      <div className="hidden md:block bg-surface-container-lowest rounded-xl border border-outline-variant overflow-hidden">
        <div className="max-h-[50vh] overflow-y-auto">
          <table className="w-full text-left">
            <thead className="sticky top-0 bg-surface-container-low z-10"><tr className="border-b"><th className="px-4 py-3 text-label-caps">Ngày</th><th className="px-4 py-3 text-label-caps">Trạng thái</th><th className="px-4 py-3 text-label-caps">Ghi chú</th></tr></thead>
            <tbody className="divide-y">
              {allData.map(item => {
                const s = STATUS_STYLE[item.trangThai] || STATUS_STYLE['null'];
                return (<tr key={item.id} className="hover:bg-surface-container-low"><td className="px-4 py-3 text-body-md font-semibold whitespace-nowrap">{formatDate(item.ngayHoc)}</td><td className="px-4 py-3"><span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-label-caps ${s.bg} ${s.text}`}><span className="material-symbols-outlined text-[14px]">{s.icon}</span>{s.label}</span></td><td className="px-4 py-3 text-caption text-on-surface-variant">{item.ghiChu || '—'}</td></tr>);
              })}
              {allData.length === 0 && <tr><td colSpan={3} className="text-center py-12">Chưa có dữ liệu</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* MOBILE TIMELINE */}
      <div className="md:hidden">
        {displayMobile.length === 0 ? (
          <div className="text-center py-12 text-on-surface-variant"><span className="material-symbols-outlined text-[48px]">inbox</span><p>Chưa có dữ liệu</p></div>
        ) : (
          <div className="relative pl-8">
            <div className="absolute left-[15px] top-2 bottom-2 w-0.5 bg-outline-variant"></div>
            {displayMobile.map((item, idx) => {
              const s = STATUS_STYLE[item.trangThai] || STATUS_STYLE['null'];
              const date = new Date(item.ngayHoc);
              return (
                <div key={item.id} className="relative pb-4 last:pb-0">
                  <div className={`absolute -left-[23px] top-1 w-5 h-5 rounded-full border-2 border-white shadow-sm ${s.bg}`}>
                    <span className={`${s.text} text-[10px] flex items-center justify-center h-full`} style={{ fontVariationSettings: "'FILL' 1" }}>{s.icon}</span>
                  </div>
                  <div className="bg-surface-container-lowest p-3 rounded-xl border border-outline-variant/30">
                    <div className="flex justify-between items-start mb-1">
                      <p className="text-body-md font-semibold">{date.toLocaleDateString('vi-VN', { weekday: 'short', day: 'numeric', month: 'numeric' })}</p>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${s.bg} ${s.text}`}>{s.label}</span>
                    </div>
                    {item.ghiChu && <p className="text-caption text-on-surface-variant italic">"{item.ghiChu}"</p>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {allData.length > 10 && !showAllMobile && (
          <button onClick={() => setShowAllMobile(true)} className="w-full mt-4 py-3 border-2 border-dashed border-outline-variant rounded-xl text-primary font-semibold text-body-md">Xem tất cả ({allData.length} buổi)</button>
        )}
      </div>
    </div>
  );

  // Nếu fullScreen, hiển thị trực tiếp
  if (fullScreen) {
    return loading ? (
      <div className="flex items-center justify-center py-20"><span className="material-symbols-outlined animate-spin text-4xl">sync</span></div>
    ) : content;
  }

  // Nếu là BottomSheet
  return (
    <BottomSheet isOpen={show} onClose={onClose} title={selectedHS ? `${selectedHS.tenThanh || ''} ${selectedHS.hoTen}` : 'Lịch sử'}>
      {selectedHS && (
        <button onClick={() => { onClose?.(); navigate(`/hoc-sinh/${selectedHS.id}`); }} className="w-full py-2.5 mb-3 bg-primary text-on-primary rounded-xl text-body-md font-semibold">
          👤 Xem hồ sơ đầy đủ →
        </button>
      )}
      {loading ? (
        <div className="flex items-center justify-center py-8"><span className="material-symbols-outlined animate-spin">sync</span></div>
      ) : content}
    </BottomSheet>
  );
};

export default DiemDanhHistory;